import React, { useState } from 'react';
import { BookingFilters as BookingFiltersType, BookingStatus, PaymentStatus } from '../../types/booking';

interface FilterPreset {
  id: string;
  name: string;
  icon: string;
  filters: Partial<BookingFiltersType>;
}

interface BookingFilterPanelProps {
  filters: BookingFiltersType;
  onFiltersChange: (filters: BookingFiltersType) => void;
  onClose?: () => void;
}

const BookingFilterPanel: React.FC<BookingFilterPanelProps> = ({
  filters,
  onFiltersChange,
  onClose
}) => {
  const [activePreset, setActivePreset] = useState<string | null>(null);

  const filterPresets: FilterPreset[] = [
    {
      id: 'todays-pickups',
      name: "Today's Pickups",
      icon: '📅',
      filters: {
        startDate: new Date(new Date().setHours(0, 0, 0, 0)),
        endDate: new Date(new Date().setHours(23, 59, 59, 999))
      }
    },
    {
      id: 'active-bookings',
      name: 'Active Bookings',
      icon: '🚗',
      filters: {
        status: BookingStatus.IN_PROGRESS
      }
    },
    {
      id: 'pending-approval',
      name: 'Pending Approval',
      icon: '⏳',
      filters: {
        requiresApproval: true
      }
    },
    {
      id: 'overdue-returns',
      name: 'Overdue Returns',
      icon: '⚠️',
      filters: {
        status: BookingStatus.IN_PROGRESS,
        endDate: new Date()
      }
    },
    {
      id: 'unpaid',
      name: 'Unpaid Bookings',
      icon: '💳',
      filters: {
        paymentStatus: PaymentStatus.PENDING
      }
    },
    {
      id: 'this-week',
      name: 'This Week',
      icon: '📆',
      filters: {
        startDate: new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
        endDate: new Date(new Date().setDate(new Date().getDate() + (6 - new Date().getDay())))
      }
    }
  ];

  const applyPreset = (preset: FilterPreset) => {
    setActivePreset(preset.id);
    onFiltersChange(preset.filters as BookingFiltersType);
  };

  const clearFilters = () => {
    setActivePreset(null);
    onFiltersChange({});
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
            <p className="text-sm text-gray-600 mt-1">Refine your booking search</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Quick Presets */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Filters</h4>
        <div className="grid grid-cols-2 gap-2">
          {filterPresets.map(preset => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset)}
              className={`px-4 py-3 text-left rounded-lg border-2 transition-all ${
                activePreset === preset.id
                  ? 'border-blue-500 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xl">{preset.icon}</span>
                <span className="text-sm font-medium">{preset.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Filters */}
      <div className="px-6 py-4 space-y-4 max-h-96 overflow-y-auto">
        <h4 className="text-sm font-semibold text-gray-700">Advanced Filters</h4>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Booking Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value as BookingStatus || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Statuses</option>
            {Object.values(BookingStatus).map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Payment Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payment Status
          </label>
          <select
            value={filters.paymentStatus || ''}
            onChange={(e) => onFiltersChange({ ...filters, paymentStatus: e.target.value as PaymentStatus || undefined })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Payment Statuses</option>
            {Object.values(PaymentStatus).map(status => (
              <option key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Date Range
          </label>
          <div className="space-y-2">
            <input
              type="date"
              value={filters.startDate ? filters.startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => onFiltersChange({ 
                ...filters, 
                startDate: e.target.value ? new Date(e.target.value) : undefined 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={filters.endDate ? filters.endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => onFiltersChange({ 
                ...filters, 
                endDate: e.target.value ? new Date(e.target.value) : undefined 
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="End Date"
            />
          </div>
        </div>

        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search by reference, customer, or notes..."
              value={filters.search || ''}
              onChange={(e) => onFiltersChange({ ...filters, search: e.target.value || undefined })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Approval Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Approval Status
          </label>
          <select
            value={filters.requiresApproval === undefined ? '' : filters.requiresApproval.toString()}
            onChange={(e) => onFiltersChange({ 
              ...filters, 
              requiresApproval: e.target.value === '' ? undefined : e.target.value === 'true' 
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All</option>
            <option value="true">Requires Approval</option>
            <option value="false">Approved</option>
          </select>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
        <button
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
            hasActiveFilters
              ? 'text-gray-700 hover:bg-gray-200'
              : 'text-gray-400 cursor-not-allowed'
          }`}
        >
          Clear All
        </button>
        <div className="text-sm text-gray-600">
          {hasActiveFilters && (
            <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
              {Object.values(filters).filter(v => v !== undefined && v !== '').length} active
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingFilterPanel;
